import React, { useState, useEffect, useMemo, useCallback, useRef, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveScopedOverride } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useCollectionBindingOverrides,
  useEditMode,
  usePage,
  buildScopeContext,
  RichTextContent,
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import type { Amenity } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import {
  hasCustomAmenityOrder,
  sortAmenities,
  toTitleCaseAmenity,
} from '../utils/sortAmenities';
import { isNonMarketingGraphic } from '../utils/sortGallery';
import { GlowTrace } from './GlowTrace';

// ============================================================================
// Types for collection-bound gallery images
// ============================================================================

interface OverviewGalleryImage {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string;
  title?: string;
  description?: string;
}

interface SliderSlide extends OverviewGalleryImage {
  image: string;
  slotPath?: string;
}

const OVERVIEW_AMENITY_TWO_COL_AT = 6;
const LIFESTYLE_LEAD_COUNT = 2;

/** True when a gallery photo is tagged as Lifestyle (ILS title convention). */
function isLifestylePhoto(photo: OverviewGalleryImage): boolean {
  return (photo.title || '').toLowerCase().includes('lifestyle');
}

/**
 * Move up to `max` lifestyle-tagged gallery photos to the front.
 * Used to fill remaining opening slots when dedicated lifestyle images are unset.
 */
function leadWithLifestyle<T extends OverviewGalleryImage>(photos: T[], max: number): T[] {
  if (max <= 0) return photos;
  const lifestyle: T[] = [];
  const rest: T[] = [];
  for (const photo of photos) {
    if (lifestyle.length < max && isLifestylePhoto(photo)) {
      lifestyle.push(photo);
    } else {
      rest.push(photo);
    }
  }
  if (lifestyle.length === 0) return photos;
  return [...lifestyle, ...rest];
}

function gallerySrc(photo: OverviewGalleryImage): string {
  return photo.image || photo.original_url || '';
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const lifestyleImage1Def: SlotGroupDefinition = {
  label: 'Lifestyle Image 1',
  description: 'Opening slide of the overview slideshow. Leave empty to skip.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A lifestyle photo of residents or everyday living at this property — people, gathering, or neighborhood life, not a vacant unit interior or amenity still. Never use logos, brand marks, floor plan drawings, line art, or unit layout graphics.',
    context: 'First slide of the Property Overview slideshow on the Home page. Opening lifestyle image. Do not reuse Lifestyle Image 2. Never use logos or floor plan layout images.',
  },
};

const lifestyleImage2Def: SlotGroupDefinition = {
  label: 'Lifestyle Image 2',
  description: 'Second slide of the overview slideshow. Leave empty to skip.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Another unused lifestyle photo of residents or everyday living at this property — people, gathering, or neighborhood life, not a vacant unit interior or amenity still. Never use logos, brand marks, floor plan drawings, line art, or unit layout graphics.',
    context: 'Second slide of the Property Overview slideshow on the Home page. Different photo from Lifestyle Image 1. Never use logos or floor plan layout images.',
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'See All Availability' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Image Slider Sub-component
// ============================================================================

function slideLayerClass(isActive: boolean) {
  return `absolute inset-0 transition-opacity duration-600 ease-in-out ${
    isActive ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
  }`;
}

function GallerySlider({
  photos,
  lifestyle1,
  lifestyle2,
}: {
  photos: SliderSlide[];
  lifestyle1: { image: string; alt: string };
  lifestyle2: { image: string; alt: string };
}) {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const count = photos.length;

  const goTo = useCallback((idx: number) => {
    if (isTransitioning || count <= 1) return;
    setIsTransitioning(true);
    setCurrent(idx);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning, count]);

  const next = useCallback(() => goTo((current + 1) % count), [current, count, goTo]);
  const prev = useCallback(() => goTo((current - 1 + count) % count), [current, count, goTo]);

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setTimeout(next, 5000);
    return () => clearTimeout(timerRef.current);
  }, [current, count, next]);

  useEffect(() => {
    if (count > 0 && current >= count) setCurrent(0);
  }, [count, current]);

  const l1Index = photos.findIndex((p) => p.slotPath === 'propertyoverview.lifestyle_image_1');
  const l2Index = photos.findIndex((p) => p.slotPath === 'propertyoverview.lifestyle_image_2');

  return (
    <div className="relative w-full h-full overflow-hidden rounded-sm group">
      {!count && <div className="w-full h-full bg-[var(--vibe-color-surface)]" />}

      {lifestyle1.image ? (
        <div className={slideLayerClass(l1Index === current)}>
          <EditableSlotGroup
            groupPath="propertyoverview.lifestyle_image_1"
            definition={lifestyleImage1Def}
            className="block h-full w-full"
          >
            <VibesitesImage
              url={lifestyle1.image}
              alt={lifestyle1.alt}
              className="w-full h-full object-cover"
              sizes="(min-width: 1024px) 50vw, 100vw"
            />
          </EditableSlotGroup>
        </div>
      ) : (
        <div
          className={`absolute top-3 left-3 z-20 ${isEditMode ? '' : 'pointer-events-none opacity-0'}`}
          aria-hidden={!isEditMode}
        >
          <EditableSlotGroup groupPath="propertyoverview.lifestyle_image_1" definition={lifestyleImage1Def}>
            <span className="inline-flex items-center rounded-full bg-white/90 px-3 py-1.5 font-body text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--vibe-color-text)] shadow-sm">
              Add lifestyle image 1
            </span>
          </EditableSlotGroup>
        </div>
      )}

      {lifestyle2.image ? (
        <div className={slideLayerClass(l2Index === current)}>
          <EditableSlotGroup
            groupPath="propertyoverview.lifestyle_image_2"
            definition={lifestyleImage2Def}
            className="block h-full w-full"
          >
            <VibesitesImage
              url={lifestyle2.image}
              alt={lifestyle2.alt}
              className="w-full h-full object-cover"
              sizes="(min-width: 1024px) 50vw, 100vw"
            />
          </EditableSlotGroup>
        </div>
      ) : (
        <div
          className={`absolute z-20 ${lifestyle1.image ? 'top-3 left-3' : 'top-12 left-3'} ${
            isEditMode ? '' : 'pointer-events-none opacity-0'
          }`}
          aria-hidden={!isEditMode}
        >
          <EditableSlotGroup groupPath="propertyoverview.lifestyle_image_2" definition={lifestyleImage2Def}>
            <span className="inline-flex items-center rounded-full bg-white/90 px-3 py-1.5 font-body text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--vibe-color-text)] shadow-sm">
              Add lifestyle image 2
            </span>
          </EditableSlotGroup>
        </div>
      )}

      {photos.map((photo, i) => {
        if (photo.slotPath) return null;
        return (
          <div key={`gallery-${i}`} className={slideLayerClass(i === current)}>
            <VibesitesImage
              url={photo.image || ''}
              alt={photo.alt_text || photo.title || ''}
              className="w-full h-full object-cover"
              sizes="(min-width: 1024px) 50vw, 100vw"
            />
          </div>
        );
      })}

      {/* Brand-tinted overlay at bottom for depth */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

      {count > 1 && (
        <>
          {/* Prev / Next arrows */}
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Previous image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Next image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function PropertyOverview() {
  const description = useContentToken('propertyoverview.description');
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const lifestyle1 = useSlotGroup('propertyoverview.lifestyle_image_1', lifestyleImage1Def);
  const lifestyle2 = useSlotGroup('propertyoverview.lifestyle_image_2', lifestyleImage2Def);
  const tabInteriorLabel = useContentToken('propertyoverview.tab_interior_label');
  const tabCommunityLabel = useContentToken('propertyoverview.tab_community_label');

  // Use useCollectionBinding instead of useGallery so the image slider
  // is editable in the Vibesites content editor (hide, reorder, add custom images).
  const galleryImages = useCollectionBinding<OverviewGalleryImage>('propertyoverview.gallery_images', {
    collection: 'photo_gallery',
    defaultLimit: 12,
  });
  // Reuse AmenitiesList binding paths so overview order matches the Amenities page
  // (including editor custom order / hide overrides).
  const page = usePage();
  const scopeCtx = buildScopeContext(page);
  const collectionOverrides = useCollectionBindingOverrides();
  const communityCustomOrder = hasCustomAmenityOrder(
    resolveScopedOverride(collectionOverrides, 'amenitieslist.communityItems', scopeCtx),
  );
  const apartmentCustomOrder = hasCustomAmenityOrder(
    resolveScopedOverride(collectionOverrides, 'amenitieslist.apartmentItems', scopeCtx),
  );

  const communityAmenitiesRaw = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });
  const apartmentAmenitiesRaw = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  // Featured / desirability sort by default; Amenities-page editor reorder wins when present.
  const communityAmenities = useMemo(
    () => sortAmenities(communityAmenitiesRaw, { customOrder: communityCustomOrder }),
    [communityAmenitiesRaw, communityCustomOrder],
  );
  const apartmentAmenities = useMemo(
    () => sortAmenities(apartmentAmenitiesRaw, { customOrder: apartmentCustomOrder }),
    [apartmentAmenitiesRaw, apartmentCustomOrder],
  );

  const showInteriorSelectHomesDisclaimer = useMemo(
    () => apartmentAmenities.some((item) => item.title?.includes('*')),
    [apartmentAmenities],
  );
  const interiorSelectHomesDisclaimer = useContentToken('propertyoverview.interior_select_homes_disclaimer');

  const lifestyle1Slide = useMemo<SliderSlide | null>(() => {
    if (!lifestyle1.image) return null;
    return {
      image: lifestyle1.image,
      alt_text: lifestyle1.alt || 'Lifestyle photo',
      slotPath: 'propertyoverview.lifestyle_image_1',
    };
  }, [lifestyle1.image, lifestyle1.alt]);

  const lifestyle2Slide = useMemo<SliderSlide | null>(() => {
    if (!lifestyle2.image) return null;
    return {
      image: lifestyle2.image,
      alt_text: lifestyle2.alt || 'Lifestyle photo',
      slotPath: 'propertyoverview.lifestyle_image_2',
    };
  }, [lifestyle2.image, lifestyle2.alt]);

  const sliderPhotos = useMemo<SliderSlide[]>(() => {
    const leading = [lifestyle1Slide, lifestyle2Slide].filter((p): p is SliderSlide => !!p);
    const used = new Set(leading.map((p) => p.image));
    const gallery: SliderSlide[] = (galleryImages || [])
      .map((p) => ({ ...p, image: gallerySrc(p) }))
      .filter(
        (p): p is SliderSlide =>
          !!p.image && !used.has(p.image) && !isNonMarketingGraphic(p),
      );
    const need = Math.max(0, LIFESTYLE_LEAD_COUNT - leading.length);
    return [...leading, ...leadWithLifestyle(gallery, need)];
  }, [galleryImages, lifestyle1Slide, lifestyle2Slide]);

  const [activeTab, setActiveTab] = useState<'interior' | 'community'>('interior');
  const [descExpanded, setDescExpanded] = useState(false);
  const activeItems = activeTab === 'interior' ? apartmentAmenities : communityAmenities;
  const useTwoColAmenities = activeItems.length >= OVERVIEW_AMENITY_TWO_COL_AT;

  useRevealStyles();
  const accentRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* ============================================================ */}
          {/* Left Column: Text + Amenity Tabs                              */}
          {/* ============================================================ */}
          <div ref={accentRef as RefObject<HTMLDivElement>} className="reveal-fade-up">
            {/* Accent line draws in above the eyebrow */}
            <svg className="accent-line accent-line--delay-1 mb-4" style={{ '--line-length': '64', width: '64px', height: '3px' } as React.CSSProperties} viewBox="0 0 64 3" preserveAspectRatio="none">
              <line x1="0" y1="1.5" x2="64" y2="1.5" strokeWidth="2" />
            </svg>

            <RichTextContent
              path="propertyoverview.pre_title"
              label="Eyebrow Text"
              defaultValue="<p>Your Search Ends Here</p>"
              as="p"
              className="font-body text-xs sm:text-sm uppercase tracking-[0.2em] text-black mb-3"
            />

            <RichTextContent
              path="propertyoverview.title"
              label="Title"
              defaultValue="<p>Experience the charm.</p>"
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-[2.75rem] font-normal text-[var(--vibe-color-text)] mb-5 leading-[1.15]"
            />

            <div className="mb-10 max-w-lg">
              <div className={`overview-desc-wrap ${descExpanded ? 'overview-desc-wrap--open' : ''}`}>
                <RichTextContent
                  path="propertyoverview.description"
                  label="Description"
                  defaultValue="<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities. Discover the perfect blend of comfort, style, and convenience.</p>"
                  as="div"
                  className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed"
                />
              </div>
              {description && String(description).length > 200 && (
                <button
                  onClick={() => setDescExpanded(!descExpanded)}
                  className="mt-2 font-body text-sm font-semibold text-[var(--vibe-color-text)] hover:underline transition-colors"
                >
                  {descExpanded ? (
                    <RichTextContent
                      path="propertyoverview.read_less_label"
                      label="Read Less Label"
                      defaultValue="<p>Read Less</p>"
                      as="span"
                    />
                  ) : (
                    <RichTextContent
                      path="propertyoverview.read_more_label"
                      label="Read More Label"
                      defaultValue="<p>Read More</p>"
                      as="span"
                    />
                  )}
                </button>
              )}
            </div>

            {/* Amenity Tabs */}
            {(apartmentAmenities.length > 0 || communityAmenities.length > 0) && (
              <div className="mb-8 max-w-lg">
                <div className="overview-tab-row flex flex-wrap gap-2">
                  {apartmentAmenities.length > 0 && (
                    <button
                      onClick={() => setActiveTab('interior')}
                      className={`overview-tab ${activeTab === 'interior' ? 'overview-tab--active' : ''}`}
                    >
                      <EditableToken path="propertyoverview.tab_interior_label" defaultValue="Interior Features" as="span">
                        {tabInteriorLabel}
                      </EditableToken>
                    </button>
                  )}
                  {communityAmenities.length > 0 && (
                    <button
                      onClick={() => setActiveTab('community')}
                      className={`overview-tab ${activeTab === 'community' ? 'overview-tab--active' : ''}`}
                    >
                      <EditableToken path="propertyoverview.tab_community_label" defaultValue="Community Amenities" as="span">
                        {tabCommunityLabel}
                      </EditableToken>
                    </button>
                  )}
                </div>

                <div className="pt-4">
                  <ul
                    className={
                      useTwoColAmenities
                        ? 'overview-amenity-list overview-amenity-list--two-col'
                        : 'overview-amenity-list'
                    }
                  >
                    {activeItems.map((a, i) => (
                      <li key={`${activeTab}-${i}`} className="overview-amenity-item flex items-start gap-2.5 text-sm font-body text-[var(--vibe-color-text-muted)] leading-snug">
                        <span className="overview-bullet mt-1.5 flex-shrink-0" />
                        <span>{a.title ? toTitleCaseAmenity(a.title) : ''}</span>
                      </li>
                    ))}
                  </ul>
                  {activeTab === 'interior' && showInteriorSelectHomesDisclaimer && (
                    <EditableToken
                      path="propertyoverview.interior_select_homes_disclaimer"
                      as="p"
                      className="overview-amenity-disclaimer"
                    >
                      {interiorSelectHomesDisclaimer || '* Available in select homes'}
                    </EditableToken>
                  )}
                </div>
              </div>
            )}

            {/* CTA */}
            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="overview-cta-btn inline-flex items-center gap-2"
              >
                <span>{ctaLink.label || 'See All Availability'}</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>

          {/* ============================================================ */}
          {/* Right Column: Gallery Slider with corner accents              */}
          {/* ============================================================ */}
          <div className="glow-trace-wrap relative w-full aspect-[4/3] lg:aspect-[3/4] lg:max-h-[640px] rounded-sm overflow-hidden">
            <GlowTrace delay={0.5} />
            <EditableCollectionBinding
              bindingPath="propertyoverview.gallery_images"
              label="Overview Slideshow Images"
              className="block h-full w-full"
            >
              <GallerySlider
                photos={sliderPhotos}
                lifestyle1={{
                  image: lifestyle1.image || '',
                  alt: lifestyle1.alt || 'Lifestyle photo',
                }}
                lifestyle2={{
                  image: lifestyle2.image || '',
                  alt: lifestyle2.alt || 'Lifestyle photo',
                }}
              />
            </EditableCollectionBinding>
          </div>
        </div>
      </div>

      <style>{`
        /* Description expand / collapse */
        .overview-desc-wrap {
          max-height: 9.6em;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }
        .overview-desc-wrap--open {
          max-height: 60em;
          -webkit-mask-image: none;
          mask-image: none;
        }

        /* Tabs */
        .overview-tab {
          padding: 0.5rem 1.25rem;
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          background: transparent;
          border: 1px solid var(--vibe-color-border);
          border-radius: 9999px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .overview-tab:hover {
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }
        .overview-tab--active {
          color: var(--vibe-color-text-on-primary);
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 78%, black) 100%
          );
          border-color: transparent;
          box-shadow: 0 1px 6px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .overview-tab--active:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: transparent;
        }

        /* Bullet */
        .overview-bullet {
          display: block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
          opacity: 0.6;
          flex-shrink: 0;
        }

        /* Amenity list — multi-column so wrapped lines don't stretch sibling rows */
        .overview-amenity-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .overview-amenity-list--two-col {
          display: block;
          column-count: 1;
          column-gap: 1rem;
        }
        @media (min-width: 640px) {
          .overview-amenity-list--two-col {
            column-count: 2;
          }
        }
        .overview-amenity-item {
          break-inside: avoid;
          -webkit-column-break-inside: avoid;
          page-break-inside: avoid;
          margin-bottom: 0.5rem;
        }
        .overview-amenity-list--two-col .overview-amenity-item:last-child {
          margin-bottom: 0;
        }

        .overview-amenity-disclaimer {
          margin: 0.75rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.45;
          color: var(--vibe-color-text-muted);
        }

        /* CTA Button */
        .overview-cta-btn {
          padding: 0.875rem 2rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%);
          border-radius: 9999px;
          transition: all 0.3s ease;
        }
        .overview-cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }

        /* Slider transition */
        .duration-600 {
          transition-duration: 600ms;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Eyebrow Text',
      type: 'richtext',
      defaultValue: '<p>Your Search Ends Here</p>',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_home_hero_title',
      defaultValue: '<p>Experience the charm.</p>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities.</p>',
    },
    'propertyoverview.tab_interior_label': {
      label: 'Interior Tab Label',
      type: 'text',
      defaultValue: 'Interior Features',
    },
    'propertyoverview.tab_community_label': {
      label: 'Community Tab Label',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'propertyoverview.interior_select_homes_disclaimer': {
      label: 'Interior Select Homes Disclaimer',
      type: 'text',
      defaultValue: '* Available in select homes',
    },
    'propertyoverview.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'propertyoverview.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
  },
  slotGroups: {
    'propertyoverview.lifestyle_image_1': lifestyleImage1Def,
    'propertyoverview.lifestyle_image_2': lifestyleImage2Def,
    'propertyoverview.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'propertyoverview.gallery_images': {
      label: 'Overview Slideshow Images',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
